/* utils/services/SerpApiClient.ts ---------------------------------------- *
 * Thread-safe wrapper around your Rails → SerpApi proxy.                   *
 * Handles *both* `local_results` and full-fat “place” payloads.            *
 * ------------------------------------------------------------------------ */

const ENDPOINT_PLACE  = '/api/serp_api/place';   // GET place_id=XXX
const ENDPOINT_SEARCH = '/api/serp_api/search';  // GET q=...&ll=@lat,lng,14z

/* ------------------------------------------------------------------ *
 *  1.  Helpful types – extend freely                                 *
 * ------------------------------------------------------------------ */

type LatLng = { lat: number; lng: number };

interface GpsCoordinates { latitude: number; longitude: number }

/** Single image thumb (works for both `images` + `photos`). */
interface ImageThumb {
  title?: string;
  thumbnail?: string;
  serpapi_thumbnail?: string;
  photo_reference?: string;        // Google raw photos
}

/** Raw Google/Serp review record. */
interface ReviewRaw {
  username?: string;
  author_name?: string;
  rating?: number;
  description?: string;
  text?: string;
  relative_time_description?: string;
  date?: string;
  images?: { thumbnail: string }[];
}

/** Shape of the new `user_reviews` block. */
interface UserReviewsRaw {
  summary?:         { snippet: string }[];
  most_relevant?:   ReviewRaw[];
}

/* -- What the rest of the app consumes -- */
export interface SerpApiPlace {
  /* Essentials ----------------------------------------------------- */
  place_id:            string;
  name:                string;
  rating?:             number;
  user_ratings_total?: number;
  formatted_address?:  string;
  gps_coordinates:     GpsCoordinates;
  website?:            string;
  phone?:              string;
  international_phone_number?: string;
  formatted_phone_number?: string;

  /* Rich media ----------------------------------------------------- */
  images?: ImageThumb[];
  photos?: ImageThumb[];

  /* Reviews -------------------------------------------------------- */
  user_reviews?: UserReviewsRaw;
  reviews?: ReviewRaw[];                 // legacy fallback

  /* Odds & ends ---------------------------------------------------- */
  plus_code?: string;
  type?: string[];
  extensions?: unknown;
}

/* Raw local-result item (pared down to what we actually use) */
interface LocalResult {
  place_id:            string;
  title:               string;
  rating?:             number;
  reviews?:            number;
  address?:            string;
  gps_coordinates:     GpsCoordinates;
  website?:            string;
  phone?:              string;
}

/* Raw “place_results” object – only list fields we normalise */
interface PlaceResultsRaw extends Omit<SerpApiPlace, 'name'> {
  title: string;                       // Google uses `title`, we expose `name`
}

/* ------------------------------------------------------------------ */
/*  2.  Singleton plumbing                                            */
/* ------------------------------------------------------------------ */

class SerpApiClient {
  private static _instance: SerpApiClient;
  static get shared(): SerpApiClient {
    if (!SerpApiClient._instance) SerpApiClient._instance = new SerpApiClient();
    return SerpApiClient._instance;
  }
  private constructor() {
    if (SerpApiClient._instance)
      throw new Error('Use SerpApiClient.shared, not constructor.');
  }

  /* ---------------------------------------------------------------- */
  /*  3.  Public API – mirrors the Swift client                       */
  /* ---------------------------------------------------------------- */

  /** Exact lookup when you already know a `place_id`. */
  async place(placeId: string): Promise<SerpApiPlace> {
    /** Serp proxy used to wrap the payload *years* ago:
     *      { place_results: { … } }
     *  Nowadays it hands back the place object directly. Support both,
     *  so older sandbox data / tests don’t explode. */
    const json = await this.#get<
      PlaceResultsRaw | { place_results: PlaceResultsRaw }
    >(ENDPOINT_PLACE, { place_id: placeId });

    if (json.error) {
      return Promise.reject(json.error)
    }

    const raw: PlaceResultsRaw =
      'place_results' in json ? json.place_results : (json as PlaceResultsRaw);

    return this.#mapPlaceResults(raw);
  }

  /** Local search around a point; falls back to `place_results`. */
  async local(opts: {
    name: string;
    otherInfo?: string;
    position: LatLng;
    maxDistance?: number;
  }): Promise<SerpApiPlace> {
    const { name, otherInfo = '', position, maxDistance = 1000 } = opts;

    const q  = otherInfo ? `${name} ${otherInfo}` : name;
    const ll = `@${position.lat},${position.lng},14z`;

    type SearchJson = {
      local_results?: LocalResult[];
      place_results?: PlaceResultsRaw;
    };
    const json = await this.#get<SearchJson>(ENDPOINT_SEARCH, { q, ll });

    if (json.error) {
      return Promise.reject(json.error)
    }

    /* 3a.  Prefer `local_results` when present --------------------- */
    if (json.local_results?.length) {
      const hit = json.local_results[0];

      // Haversine check
      const toRad = (x: number) => (x * Math.PI) / 180;
      const dLat  = toRad(hit.gps_coordinates.latitude  - position.lat);
      const dLon  = toRad(hit.gps_coordinates.longitude - position.lng);
      const a     =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(position.lat)) *
        Math.cos(toRad(hit.gps_coordinates.latitude)) *
        Math.sin(dLon / 2) ** 2;
      const dist = 6_371_000 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

      if (dist > maxDistance)
        throw new Error(
          `Closest result at ${dist.toFixed()} m exceeds ${maxDistance} m`,
        );

      return this.#mapLocalResult(hit);
    }

    /* 3b.  Fallback: `place_results` ------------------------------- */
    if (json.place_results)
      return this.#mapPlaceResults(json.place_results);

    throw new Error('No results returned.');
  }

  /* ---------------------------------------------------------------- */
  /*  4.  Helpers                                                     */
  /* ---------------------------------------------------------------- */

  async #get<T>(endpoint: string, params: Record<string, string>): Promise<T> {
    const qs  = new URLSearchParams(params).toString();
    const res = await fetch(`${endpoint}?${qs}`);
    if (!res.ok) throw new Error(`Proxy HTTP ${res.status}`);
    return res.json() as Promise<T>;
  }

  #mapLocalResult(hit: LocalResult): SerpApiPlace {
    return {
      place_id:            hit.place_id,
      name:                hit.title,
      rating:              hit.rating,
      user_ratings_total:  hit.reviews,
      formatted_address:   hit.address,
      gps_coordinates:     hit.gps_coordinates,
      website:             hit.website,
      phone:               hit.phone,
      international_phone_number: hit.phone,
      formatted_phone_number: hit.phone,
    };
  }

  #mapPlaceResults(raw: PlaceResultsRaw): SerpApiPlace {
    return {
      place_id:            raw.place_id,
      name:                raw.title,
      rating:              raw.rating,
      user_ratings_total:  raw.reviews,
      formatted_address:   raw.address,
      gps_coordinates:     raw.gps_coordinates ?? { latitude: 0, longitude: 0 },
      website:             raw.website,
      phone:               raw.phone,
      international_phone_number: raw.international_phone_number,
      formatted_phone_number: raw.formatted_phone_number,
      user_reviews: raw.user_reviews,
      images: raw.images
    };
  }
}

export const serpClient = SerpApiClient.shared;
